import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";

// AWS cron day-of-week: 1-7 = SUN-SAT, or names.
const DAY_INDEX: Record<string, number> = {
  "1": 0,
  "2": 1,
  "3": 2,
  "4": 3,
  "5": 4,
  "6": 5,
  "7": 6,
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

interface ParsedSchedule {
  utcHour: number;
  utcMinute: number;
  // Eastern weekday name for weekly rules; null for daily
  weekday: string | null;
}

// Parses the fixed-time crons the backend writes: daily "cron(M H * * ? *)"
// or weekly "cron(M H ? * MON *)". Null for anything else (the pill hides).
const parseScheduleExpression = (expression: string): ParsedSchedule | null => {
  const match = expression.match(
    /^cron\((\d{1,2}) (\d{1,2}) (\S+) (\S+) (\S+) \S+\)$/
  );
  if (!match) {
    return null;
  }
  const [, minute, hour, dayOfMonth, , dayOfWeek] = match;
  const utcHour = parseInt(hour, 10);
  const utcMinute = parseInt(minute, 10);

  if (dayOfMonth === "*" && (dayOfWeek === "?" || dayOfWeek === "*")) {
    return { utcHour, utcMinute, weekday: null };
  }
  const dayIndex = DAY_INDEX[dayOfWeek.toUpperCase()];
  if (dayOfMonth === "?" && dayIndex !== undefined) {
    // UTC → Eastern can cross midnight backwards (e.g. TUE 02:00 UTC is
    // Monday evening ET), shifting the weekday back one.
    const scheduled = scheduledUtcDate(utcHour, utcMinute);
    const easternHour = parseInt(
      new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        hour: "numeric",
        hour12: false,
      }).format(scheduled),
      10
    );
    const crossesBack = easternHour > utcHour;
    return {
      utcHour,
      utcMinute,
      weekday: WEEKDAYS[crossesBack ? (dayIndex + 6) % 7 : dayIndex],
    };
  }
  return null;
};

const scheduledUtcDate = (utcHour: number, utcMinute: number): Date => {
  const d = new Date();
  d.setUTCHours(utcHour, utcMinute, 0, 0);
  return d;
};

// "14:00" — value for <input type="time">, in Eastern.
const easternInputValue = (utcHour: number, utcMinute: number): string =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(scheduledUtcDate(utcHour, utcMinute));

// "2:00 PM EDT" — display label, in Eastern.
const easternDisplay = (utcHour: number, utcMinute: number): string =>
  new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(scheduledUtcDate(utcHour, utcMinute));

export const RunScheduleComponent = (props: {
  scheduleExpression: string;
  isEnabled: boolean;
  saving: boolean;
  onSave: (hourEastern: number, minuteEastern: number) => Promise<boolean>;
}) => {
  const { scheduleExpression, isEnabled, saving, onSave } = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [timeValue, setTimeValue] = useState("");

  const parsed = parseScheduleExpression(scheduleExpression);
  // Monthly / multi-day / exotic rules aren't editable here — hide the pill
  // rather than show a control that can't apply.
  if (!parsed) {
    return null;
  }

  const cadenceLabel = parsed.weekday ? `${parsed.weekday}s` : "Daily run";
  const label = `${cadenceLabel} · ${easternDisplay(
    parsed.utcHour,
    parsed.utcMinute
  )}${isEnabled ? "" : " (paused)"}`;

  const handleOpen = () => {
    setTimeValue(easternInputValue(parsed.utcHour, parsed.utcMinute));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const match = timeValue.match(/^(\d{2}):(\d{2})$/);
    if (!match) {
      return;
    }
    const succeeded = await onSave(
      parseInt(match[1], 10),
      parseInt(match[2], 10)
    );
    if (succeeded) {
      setDialogOpen(false);
    }
  };

  return (
    <>
      <Tooltip title="Change when your scheduled run starts (Eastern Time)">
        <Button
          onClick={handleOpen}
          startIcon={<ScheduleIcon sx={{ fontSize: 16 }} />}
          sx={{
            height: 34,
            px: "10px",
            textTransform: "none",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
            color: (t: Theme) => t.palette.atomic.ink,
            backgroundColor: (t: Theme) => t.palette.atomic.panelTint,
            border: (t: Theme) => `1px solid ${t.palette.atomic.border}`,
            borderRadius: "6px",
          }}
        >
          {label}
        </Button>
      </Tooltip>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Scheduled run time</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 2 }}>
            Your {parsed.weekday ? `${parsed.weekday} ` : "daily "}
            run starts at this time, Eastern Time. Data pulls are scheduled
            separately, so pick a time after your data usually arrives.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              type="time"
              size="small"
              value={timeValue}
              onChange={(e) => setTimeValue(e.target.value)}
              inputProps={{ step: 300 }}
            />
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
              Eastern Time
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !/^\d{2}:\d{2}$/.test(timeValue)}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
